import React, { useMemo, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import { useConfig } from '../../contexts/ConfigContext';
import { getSpecializationTerm, getSpecializationPrefix } from '../../utils/organizationHelpers';
import GroupsList from '../../components/GroupsList';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import Footer from '../../components/Footer';
import '../MainPage/MainPage.css';
import './ArticlePage.css';

const scrollToId = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const ArticlePage = () => {
    useIframeCommunication();
    const { id } = useParams();
    const navigate = useNavigate();
    const { config } = useConfig();
    const orgCode = config?.code || 'mag';

    const [selectedDirection, setSelectedDirection] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);

    const goBackToArticles = useCallback(() => {
        if (document.referrer) {
            navigate(-1);
            return;
        }
        navigate('/public/articles');
    }, [navigate]);

    const { data: article, isLoading } = useQuery(
        ['article', id],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/articles/${id}`);
            return response.data;
        }
    );

    const authors = useMemo(() => {
        if (!article) return [];
        if (Array.isArray(article.authors) && article.authors.length > 0) {
            return article.authors;
        }
        if (article.author_id || article.author_name) {
            return [{
                id: article.author_id,
                name: article.author_name,
                photo_url: article.author_photo
            }];
        }
        return [];
    }, [article]);

    const authorIds = useMemo(
        () => authors.map((a) => a.id).filter(Boolean),
        [authors]
    );

    const { data: allGroupsRaw } = useQuery(
        ['articleAuthorGroups', authorIds.join(',')],
        async () => {
            const results = await Promise.all(
                authorIds.map(async (authorId) => {
                    const response = await axios.get(`${API_BASE_URL}/users/${authorId}/teacher-groups`);
                    return response.data || [];
                })
            );
            const byId = new Map();
            results.flat().forEach((group) => {
                if (group?.id != null && !byId.has(group.id)) {
                    byId.set(group.id, group);
                }
            });
            return [...byId.values()];
        },
        {
            enabled: authorIds.length > 0 && orgCode === 'nasledie',
            retry: 1
        }
    );

    const { data: directions } = useQuery(
        'publicDirections',
        () => axios.get(`${API_BASE_URL}/directions/public`).then((res) => res.data),
        { enabled: orgCode === 'nasledie' && authorIds.length > 0 }
    );

    const allGroups = useMemo(() => {
        if (!allGroupsRaw) return [];
        return allGroupsRaw.filter((group) => {
            const status = group.recruitment_status;
            if (status !== 'open' && status !== 'started_additional') return false;
            const hasRegularFormat =
                group.format_data?.[0]?.frequency === 'weekly' ||
                group.format_data?.[0]?.frequency === 'biweekly';
            return group.earliest_event_date || hasRegularFormat;
        });
    }, [allGroupsRaw]);

    const displayDirections = useMemo(() => {
        if (!directions || !allGroups) return [];

        const prefix = getSpecializationPrefix(orgCode);
        const specializationTerm = getSpecializationTerm(orgCode);
        const availableDirectionIds = new Set(allGroups.map((g) => g.direction_id).filter(Boolean));
        const availableDirections = directions.filter((d) => availableDirectionIds.has(d.id));
        const specializations = availableDirections.filter((d) => d.name.startsWith(prefix));
        const otherDirections = availableDirections.filter((d) => !d.name.startsWith(prefix));

        const result = [];
        if (specializations.length > 0) {
            result.push({
                id: 'specialization',
                name: specializationTerm,
                directionIds: specializations.map((s) => s.id)
            });
        }
        otherDirections.forEach((d) => {
            result.push({ id: d.id, name: d.name, directionIds: [d.id] });
        });
        return result;
    }, [directions, allGroups, orgCode]);

    const webinarDirection = useMemo(
        () => displayDirections.find((d) => /вебинар/i.test(d.name || '')),
        [displayDirections]
    );

    const selectedDirectionIds = useMemo(() => {
        if (!selectedDirection) return null;
        const direction = displayDirections.find((d) => d.id === selectedDirection);
        return direction ? direction.directionIds : null;
    }, [selectedDirection, displayDirections]);

    const groups = useMemo(() => {
        if (!allGroups) return [];

        const filtered = allGroups.filter((group) => {
            if (selectedDirectionIds && !selectedDirectionIds.includes(group.direction_id)) {
                return false;
            }
            if (selectedRegion) {
                const regionName = group.region_name || '';
                const hasMoscow = regionName.includes('Москва');
                const hasOnline = regionName.includes('Онлайн');
                if (selectedRegion === 'Москва' && !hasMoscow) return false;
                if (selectedRegion === 'Онлайн' && !hasOnline) return false;
                if (selectedRegion === 'Регионы' && (hasMoscow || hasOnline)) return false;
            }
            return true;
        });

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const getKey = (g) => {
            if (!g.earliest_event_date) return { bucket: 1, time: Number.MAX_SAFE_INTEGER };
            const d = new Date(g.earliest_event_date);
            d.setHours(0, 0, 0, 0);
            const ts = d.getTime();
            if (ts >= today.getTime()) return { bucket: 0, time: ts };
            return { bucket: 2, time: ts };
        };

        return [...filtered].sort((a, b) => {
            const ka = getKey(a);
            const kb = getKey(b);
            if (ka.bucket !== kb.bucket) return ka.bucket - kb.bucket;
            if (ka.bucket === 0) return ka.time - kb.time;
            if (ka.bucket === 2) return kb.time - ka.time;
            return 0;
        });
    }, [allGroups, selectedDirectionIds, selectedRegion]);

    const handleDirectionClick = useCallback((directionId) => {
        setSelectedDirection((prev) => (prev === directionId ? null : directionId));
    }, []);

    const handleRegionClick = useCallback((regionKey) => {
        setSelectedRegion((prev) => (prev === regionKey ? null : regionKey));
    }, []);

    const goToGroups = useCallback(() => {
        setSelectedDirection(null);
        scrollToId('article-groups');
    }, []);

    const goToWebinars = useCallback(() => {
        if (webinarDirection) {
            setSelectedDirection(webinarDirection.id);
        }
        scrollToId('article-groups');
    }, [webinarDirection]);

    if (isLoading) {
        return (
            <div className="article-page article-page--nasledie">
                <div className="loading">
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="article-page article-page--nasledie">
                <div className="error-container">
                    <h2>Статья не найдена</h2>
                    <button type="button" className="btn btn-primary" onClick={goBackToArticles}>
                        <ArrowLeft size={18} />
                        Вернуться к статьям
                    </button>
                </div>
            </div>
        );
    }

    const hasGroups = orgCode === 'nasledie' && allGroups.length > 0;
    const authorsWithProfile = authors.filter((a) => a.id);

    return (
        <div className="article-page article-page--nasledie">
            <div className="article-page-inner">
                <button type="button" className="back-link" onClick={goBackToArticles}>
                    <ArrowLeft size={18} />
                    Вернуться к статьям
                </button>

                <article className="article-content">
                    <header className="article-header">
                        <h1>{article.title}</h1>
                        <div className="article-meta">
                            <div className="article-authors">
                                {authors.map((author) => (
                                    <div key={author.id || author.name} className="article-author">
                                        {author.id ? (
                                            <Link
                                                to={`/public/profile/${author.id}`}
                                                className="article-author-link"
                                            >
                                                {author.photo_url ? (
                                                    <img src={author.photo_url} alt={author.name} />
                                                ) : (
                                                    <div className="article-author-placeholder">
                                                        <User size={20} />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="article-author-name">{author.name}</div>
                                                </div>
                                            </Link>
                                        ) : (
                                            <>
                                                {author.photo_url ? (
                                                    <img src={author.photo_url} alt={author.name} />
                                                ) : (
                                                    <div className="article-author-placeholder">
                                                        <User size={20} />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="article-author-name">{author.name}</div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                                {article.published_at && (
                                    <div className="article-date">
                                        <Calendar size={14} />
                                        {format(new Date(article.published_at), 'd MMMM yyyy', { locale: ru })}
                                    </div>
                                )}
                            </div>

                            <div className="article-author-actions">
                                {authorsWithProfile.map((author) => (
                                    <Link
                                        key={`profile-${author.id}`}
                                        to={`/public/profile/${author.id}`}
                                        className="article-nav-btn article-nav-btn--link"
                                    >
                                        {authorsWithProfile.length > 1
                                            ? `Профиль: ${author.name}`
                                            : 'Профиль автора'}
                                    </Link>
                                ))}
                                {hasGroups && (
                                    <>
                                        <button type="button" className="article-nav-btn" onClick={goToGroups}>
                                            Группы
                                        </button>
                                        <button type="button" className="article-nav-btn" onClick={goToWebinars}>
                                            Вебинары
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </header>

                    <div
                        className="article-body"
                        dangerouslySetInnerHTML={{ __html: article.content }}
                    />
                </article>
            </div>

            {hasGroups && (
                <section className="article-groups-section" id="article-groups">
                    <div className="article-page-inner">
                        <h2 className="article-groups-title">Группы</h2>
                        <div className="main-page article-groups-main">
                            <div className="filter-buttons">
                                <h4>Какая группа вас интересует?</h4>
                                <br />
                                <div className="button-row">
                                    {displayDirections.map((direction) => (
                                        <button
                                            key={direction.id}
                                            type="button"
                                            className={selectedDirection === direction.id ? 'active-filter' : ''}
                                            onClick={() => handleDirectionClick(direction.id)}
                                        >
                                            {direction.name}
                                        </button>
                                    ))}
                                </div>
                                <br />
                                <h4>Место проведения группы:</h4>
                                <br />
                                <div className="button-row">
                                    <button
                                        type="button"
                                        className={selectedRegion === 'Москва' ? 'active-filter' : ''}
                                        onClick={() => handleRegionClick('Москва')}
                                    >
                                        Москва
                                    </button>
                                    <button
                                        type="button"
                                        className={selectedRegion === 'Онлайн' ? 'active-filter' : ''}
                                        onClick={() => handleRegionClick('Онлайн')}
                                    >
                                        Онлайн
                                    </button>
                                    <button
                                        type="button"
                                        className={selectedRegion === 'Регионы' ? 'active-filter' : ''}
                                        onClick={() => handleRegionClick('Регионы')}
                                    >
                                        Регионы
                                    </button>
                                </div>
                                <br />
                                <hr width="100%" />
                                <br />
                            </div>
                            <div>
                                <GroupsList groups={groups} orgCode={orgCode} />
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <Footer />
        </div>
    );
};

export default ArticlePage;
